import moment from 'moment'
import * as yup from 'yup'

import { validateNfseDescriptionVariants } from 'components/PopoverNewVariant/variants'

import {
  cycleIntervalBetween,
  FREQUENCY_ENUM_TO_NAME,
  FrequencyUnit,
  presetToPair
} from 'utils/frequency'

import { getCreditCardPaymentOptionsText } from './sharedCharge.utils'

// Schema compartilhado para os campos que serão movidos para SharedChargeFields
export const sharedChargeSchema = yup.object({
  // Items validation - condicional baseada em isProductOrLink e isLink
  items: yup.array().when(['$isProductOrLink', '$isLink'], {
    is: (isProductOrLink: boolean, isLink: boolean) =>
      !isProductOrLink || isLink, // Validar quando NÃO for produto/link OU quando for link
    then: yup
      .array()
      .of(
        yup
          .object()
          .shape({
            description: yup.string().nullable(),
            product: yup
              .object()
              .shape({
                value: yup.string().required('Produto obrigatório.').nullable(),
                label: yup.string().required('Produto obrigatório.').nullable()
              })
              .nullable()
              .default(null),
            price: yup
              .number()
              .transform((value, originalValue) => {
                // Se o valor original for string vazia ou null/undefined, retorna undefined
                // para que a validação .required() funcione corretamente
                if (
                  originalValue === '' ||
                  originalValue === null ||
                  originalValue === undefined
                ) {
                  return undefined
                }
                return value
              })
              .required('Informe o preço do item.')
              .min(0, 'O preço não pode ser negativo.'),
            quantity: yup
              .number()
              .transform((value, originalValue) => {
                // Se o valor original for string vazia ou null/undefined, retorna undefined
                // para que a validação .required() funcione corretamente
                if (
                  originalValue === '' ||
                  originalValue === null ||
                  originalValue === undefined
                ) {
                  return undefined
                }
                return value
              })
              .required('Informe a quantidade do item.')
              .test(
                'min-quantity',
                'A quantidade deve ser maior que 0.',
                function (value) {
                  if (value === undefined || value === null) return true
                  // Para links de contratação, permitir 0
                  const isLink = this.options.context?.isLink
                  if (isLink) {
                    return value >= 0
                  }
                  // Para outros formulários, deve ser maior que 0
                  return value > 0
                }
              ),
            total: yup.number().required('O total do item é obrigatório.'),
            typedValue: yup.string(),
            uuid: yup.string(),
            enable_customer_select_quantity: yup.boolean().optional(),
            tags: yup.array().optional(),
            start_cycle: yup.number().min(1).optional(),
            total_cycles: yup.number().min(0).optional()
          })
          .test(
            'description-or-product',
            'É necessário selecionar um produto ou preencher a descrição.',
            (value) => !!value.description || !!value.product?.value
          )
      )
      .min(1, 'É necessário ao mínimo um item'),
    otherwise: yup.array().default([]) // Para produtos (não links), apenas um array vazio
  }),

  // Description validation
  description: yup.string(),
  billingDescription: yup.string(),

  // Frequency and cycles validation
  totalCycles: yup.number().required('Total de ciclos é obrigatório.'),
  totalCyclesCustom: yup.string().when('totalCycles', {
    is: 13,
    then: yup
      .string()
      .required('Total de ciclos personalizado é obrigatório.')
      .test(
        'custom-cycles-range',
        'Para ciclos personalizados os ciclos permitidos são entre 13 e 120',
        function (value) {
          if (!value) return false
          const numValue = parseInt(value, 10)
          return numValue >= 13 && numValue <= 120
        }
      ),
    otherwise: yup.string().optional()
  }),
  frequency: yup
    .number()
    .test(
      'itens-com-cadencia-compativel',
      'Há itens cujas datas não coincidem com esta frequência. Ajuste a frequência ou remova os itens.',
      function (frequency) {
        if (frequency === undefined || frequency === null) return true

        const form = this.parent as {
          items?: any[]
          frequency_type?: FrequencyUnit
          frequency_number?: number
        }

        const preset = FREQUENCY_ENUM_TO_NAME[Number(frequency)] ?? 'monthly'
        const recurrencePair = presetToPair(
          preset,
          form.frequency_type,
          Number(form.frequency_number) || undefined
        )

        // Bloqueia o submit no mesmo critério do aviso do formulário: a API também recusa,
        // mas descobrir isso só no submit deixa o merchant sem saber qual item tirar.
        return !(form.items || []).some((item) => {
          const product = item?.product
          if (
            !product?.value ||
            product.value === 'custom' ||
            !product.frequency
          )
            return false
          // Plano de cobrança única não impõe cadência.
          if (product.total_cycles === 1 || product.total_cycles === undefined)
            return false

          return (
            cycleIntervalBetween(
              recurrencePair,
              presetToPair(
                product.frequency,
                product.frequency_type,
                Number(product.frequency_number) || undefined
              )
            ) === null
          )
        })
      }
    ),

  // Due date validation
  dueDate: yup
    .date()
    .typeError('Insira uma data válida')
    .min(
      moment(new Date()).format('YYYY-MM-DD'),
      'Selecione uma data a partir de hoje'
    )
    .required('Data de vencimento obrigatória'),

  // Expiration settings validation
  expirationField: yup.string().optional().nullable(),

  // Payments validation (manter para compatibilidade)
  payments: yup
    .array()
    .min(1, 'Pelo menos um meio de pagamento deve ser selecionado.')
    .required('Meio de pagamento obrigatório.'),

  // Nova estrutura de pagamentos (payment_type_details)
  payment_type_details: yup
    .object({
      // Recebimento manual (previsão): sem parcelamento, porque nada é emitido.
      direct_payment: yup
        .object({
          enabled: yup.boolean().optional()
        })
        .optional(),
      pix: yup
        .object({
          enabled: yup.boolean().optional(),
          fixed_installments: yup.boolean().optional(),
          max_installments: yup
            .number()
            .when('enabled', {
              is: true,
              then: yup
                .number()
                .min(1, 'Pix: mínimo 1 parcela.')
                .max(12, 'Pix: máximo 12 parcelas.')
                .required('Pix: número de parcelas é obrigatório.'),
              otherwise: yup.number().optional().nullable()
            })
            .test(
              'fixed-requires-min-2',
              'Pix: a quantidade fixa deve ser de pelo menos 2 parcelas.',
              function (value: any) {
                if (!this.parent?.fixed_installments) return true
                return Number(value) >= 2
              }
            )
        })
        .optional(),
      boleto: yup
        .object({
          enabled: yup.boolean().optional(),
          fixed_installments: yup.boolean().optional(),
          max_installments: yup
            .number()
            .when('enabled', {
              is: true,
              then: yup
                .number()
                .min(1, 'Boleto: mínimo 1 parcela.')
                .max(12, 'Boleto: máximo 12 parcelas.')
                .required('Boleto: número de parcelas é obrigatório.'),
              otherwise: yup.number().optional().nullable()
            })
            .test(
              'fixed-requires-min-2',
              'Boleto: a quantidade fixa deve ser de pelo menos 2 parcelas.',
              function (value: any) {
                if (!this.parent?.fixed_installments) return true
                return Number(value) >= 2
              }
            )
        })
        .optional(),
      boleto_pix: yup
        .object({
          enabled: yup.boolean().optional(),
          max_installments: yup.number().optional().nullable()
        })
        .optional(),
      credit_card: yup
        .object({
          enabled: yup.boolean().optional(),
          automatic_renew: yup.boolean().optional(),
          fixed_installments: yup.boolean().optional(),
          pass_card_fees: yup.boolean().optional(),
          max_installments: yup
            .mixed()
            .when('enabled', {
              is: true,
              then: yup.mixed(),
              otherwise: yup.mixed().optional().nullable()
            })
            .test(
              'fixed-requires-min-2',
              'Cartão: a quantidade fixa deve ser de pelo menos 2 parcelas.',
              function (value: any) {
                if (!this.parent?.fixed_installments) return true
                return Number(value) >= 2
              }
            ),
          max_installments_without_fee: yup.mixed().when('enabled', {
            is: true,
            then: yup
              .mixed()
              .test(
                'lte-max-installments',
                'Cartão: parcelas sem juros deve ser menor ou igual ao máximo de parcelas.',
                function (value: any) {
                  // Com "Repassar taxas do Cartão" desligado o campo é
                  // irrelevante: o payload sobrescreve max_installments_without_fee
                  // com max_installments (ver sharedCharge.utils.ts). Validar aqui
                  // só produzia erro sobre um campo que nem aparece na tela.
                  if (!this.parent?.pass_card_fees) return true

                  const maxInst = this.resolve(
                    yup.ref('max_installments')
                  ) as any
                  if (value == null || maxInst == null) return true
                  return Number(value) <= Number(maxInst)
                }
              ),
            otherwise: yup.mixed().optional().nullable()
          }),
          credit_card: yup
            .object({
              enabled: yup.boolean().optional()
            })
            .optional(),
          credit_card_installments: yup
            .object({
              enabled: yup.boolean().optional()
            })
            .optional(),
          credit_card_recurring: yup
            .object({
              enabled: yup.boolean().optional()
            })
            .optional()
        })
        .test('credit-card-recurrence-option', function (value) {
          // Só valida se o cartão estiver habilitado
          if (!value?.enabled) return true

          // Obter totalCycles do contexto ou do parent
          const rootValue = (this as any).from?.[1]?.value
          const totalCycles = rootValue?.totalCycles

          // Se totalCycles é undefined ou 1, é cobrança única
          // Para cobrança única à vista (1 parcela), não validar - pagamento manual é automático
          // max_installments pode chegar como texto ("1") do select
          const maxInstallments = Number(value.max_installments) || 0
          if (totalCycles === undefined || totalCycles === 1) {
            // Se tiver apenas 1 parcela, não precisa validar switches
            if (maxInstallments <= 1) {
              return true
            }
            // Se tiver mais de 1 parcela, continua para validar os switches abaixo
          }

          // Para recorrências (totalCycles > 1), sempre validar
          // Para cobranças únicas com parcelas > 1, também validar

          // Verificar se pelo menos uma opção está ativa
          const hasInstallmentBilling = value.credit_card_installments?.enabled
          const hasRecurringBilling = value.credit_card_recurring?.enabled
          const hasMonthlyPayment = value.credit_card?.enabled

          const isValid = !!(
            hasInstallmentBilling ||
            hasRecurringBilling ||
            hasMonthlyPayment
          )

          if (!isValid) {
            const isRecurrence = totalCycles !== undefined && totalCycles !== 1
            const hasInstallments = maxInstallments > 1
            // SC-10012: mensagem vazia não virava aviso e o salvar ficava mudo
            const errorMessage =
              getCreditCardPaymentOptionsText(isRecurrence, hasInstallments) ||
              'Selecione pelo menos uma opção de pagamento para o Cartão de Crédito'
            return this.createError({ message: errorMessage })
          }

          return true
        })
        .optional()
    })
    .test(
      'at-least-one-payment',
      'Pelo menos um meio de pagamento deve ser habilitado.',
      (value) => {
        if (!value) return false
        return !!(
          value.direct_payment?.enabled ||
          value.pix?.enabled ||
          value.boleto?.enabled ||
          value.boleto_pix?.enabled ||
          value.credit_card?.enabled
        )
      }
    )
    // Recebimento manual (previsão) não convive com meio que emite: a cobrança
    // emitiria de verdade E ganharia um recebimento manual em aberto. O backend
    // valida o mesmo invariante (model + controller).
    .test(
      'direct-payment-is-exclusive',
      'Recebimento manual não pode ser combinado com outros meios de pagamento.',
      (value) => {
        if (!value?.direct_payment?.enabled) return true
        return !(
          value.pix?.enabled ||
          value.boleto?.enabled ||
          value.boleto_pix?.enabled ||
          value.credit_card?.enabled
        )
      }
    )
    .optional(),

  // Credit card validation
  credit_card_uuid: yup.object(),

  // Discount policy validation
  discountPolicy: yup.boolean(),
  discounts: yup.array().when('discountPolicy', {
    is: true,
    then: yup
      .array()
      .of(
        yup.object().shape({
          amount: yup.string().required('Valor do desconto obrigatório.'),
          type: yup.string().required('Tipo do desconto obrigatório.'),
          date: yup.date().when(['totalCycles', '$isProductOrLink'], {
            is: (totalCycles: number, isProductOrLink: boolean) =>
              totalCycles === 1 && !isProductOrLink,
            then: yup.date().required('Data do desconto obrigatória.'),
            otherwise: yup.date().nullable()
          }),
          // `$isCharge === false` é a recorrência do modal de cliente: lá o desconto
          // é escolhido em dias antes do vencimento e não pode ir em branco (SC-11660).
          days: yup
            .string()
            .when(['totalCycles', '$isProductOrLink', '$isCharge'], {
              is: (
                totalCycles: number,
                isProductOrLink: boolean,
                isCharge: boolean | undefined
              ) => totalCycles > 1 || isProductOrLink || isCharge === false,
              then: yup.string().required('Dias do desconto obrigatório.'),
              otherwise: yup.string().nullable()
            }),
          totalCycles: yup.number().min(0, 'Mínimo 0').default(0),
          startCycle: yup
            .number()
            .min(1, 'Ciclo inicial deve ser no mínimo 1')
            .default(1)
        })
      )
      .min(1, 'Adicione pelo menos um desconto.'),
    otherwise: yup
      .array()
      .of(
        yup.object().shape({
          amount: yup.string().nullable(),
          type: yup.string().nullable(),
          date: yup.date().nullable(),
          days: yup.string().nullable(),
          totalCycles: yup.number().min(0, 'Mínimo 0').default(0),
          startCycle: yup
            .number()
            .min(1, 'Ciclo inicial deve ser no mínimo 1')
            .default(1)
        })
      )
      .default([])
  }),

  // Interest policy validation
  interestPolicy: yup.boolean(),
  interestFee: yup.string().when('interestPolicy', {
    is: true,
    otherwise: yup.string(),
    then: yup.string().required('Taxa de juros é obrigatória.')
  }),
  interestFine: yup.string().when('interestPolicy', {
    is: true,
    otherwise: yup.string(),
    then: yup.string().required('Multa é obrigatória.')
  }),

  // Negativation validation
  interestNegativation: yup.boolean(),
  daysToReport: yup.string().when('interestNegativation', {
    is: true,
    otherwise: yup.string(),
    then: yup.string().required('Dias para negativação é obrigatório.')
  }),

  // Bonus validation
  hasBonus: yup.boolean().default(false),
  bonusSettings: yup.object().when('hasBonus', {
    is: true,
    then: yup.object().shape({
      cyclesCount: yup
        .number()
        .required('Ciclos da bonificação é obrigatório.'),
      cyclesCountCustom: yup.string().when('cyclesCount', {
        is: 13,
        then: yup
          .string()
          .required('Ciclos personalizados da bonificação é obrigatório.')
          .test(
            'custom-cycles-range',
            'Quantidade de ciclos personalizados inválida.',
            function (value) {
              if (!value) return false
              const numValue = parseInt(value, 10)

              if (numValue < 13) {
                return this.createError({
                  message: 'Para ciclos personalizados o mínimo é 13.'
                })
              }

              // The bonus only covers cycles from startCycle onward, so the upper
              // bound is the cycles remaining from startCycle; for infinite
              // recurrences it stays at 120.
              // `from` exists at runtime (yup 0.32) but isn't in its TS types.
              const from = (this as unknown as { from?: { value: any }[] }).from
              const rootValues = from?.[from.length - 1]?.value
              // The recurrence's own cycle count uses 13 as an "Outros" sentinel,
              // storing the real number in totalCyclesCustom (e.g. a 31-cycle
              // recurrence has totalCycles === 13 and totalCyclesCustom === '31').
              const rawTotalCycles = Number(rootValues?.totalCycles) || 0
              const totalCycles =
                rawTotalCycles === 13
                  ? Number(rootValues?.totalCyclesCustom) || 0
                  : rawTotalCycles
              const startCycle =
                Number(rootValues?.bonusSettings?.startCycle) || 1
              const remaining =
                totalCycles > 0 ? Math.max(totalCycles - startCycle + 1, 0) : 0
              const upperBound = totalCycles > 0 ? remaining : 120

              if (numValue > upperBound) {
                return this.createError({
                  message:
                    totalCycles <= 0
                      ? 'Para ciclos personalizados o máximo é 120.'
                      : startCycle > 1
                      ? `A partir do ciclo ${startCycle}, restam ${upperBound} ciclos. Informe um valor entre 13 e ${upperBound}.`
                      : `A recorrência possui ${upperBound} ciclos. Informe um valor entre 13 e ${upperBound}.`
                })
              }

              return true
            }
          ),
        otherwise: yup.string().optional()
      }),
      type: yup
        .string()
        .oneOf(['value', 'percentage'], 'Tipo de bonificação inválido.')
        .required('Tipo da bonificação é obrigatório.'),
      amount: yup
        .string()
        .test(
          'not-empty-or-zero',
          'Valor da bonificação é obrigatório.',
          function (value) {
            const rootValue = (this as any).from?.[1]?.value
            const hasBonus = rootValue?.hasBonus

            if (!hasBonus) return true

            if (!value || value === '' || value === '0' || value === '0,00') {
              return false
            }
            return true
          }
        )
        .test(
          'max-percentage',
          'O valor da bonificação deve ser menor ou igual a 100% do valor total.',
          function (value) {
            const rootValue = (this as any).from?.[1]?.value
            const hasBonus = rootValue?.hasBonus

            if (!hasBonus) return true

            const { type } = this.parent
            const items = rootValue?.items
            let totalValue = this.options.context?.totalValue

            if (!totalValue && items && Array.isArray(items)) {
              totalValue = items.reduce((sum: number, item: any) => {
                const total = item.total || 0
                return sum + Number(total)
              }, 0)
            }

            if (!value || !totalValue) return true

            if (type === 'percentage') {
              const percentageValue = parseFloat(value.replace(',', '.'))
              if (isNaN(percentageValue) || percentageValue > 100) {
                return false
              }
            }

            return true
          }
        )
        .test(
          'max-value',
          'O valor da bonificação deve ser menor ou igual a 100% do valor total.',
          function (value) {
            const rootValue = (this as any).from?.[1]?.value
            const hasBonus = rootValue?.hasBonus

            if (!hasBonus) return true

            const { type } = this.parent
            const items = rootValue?.items
            let totalValue = this.options.context?.totalValue

            if (!totalValue && items && Array.isArray(items)) {
              totalValue = items.reduce((sum: number, item: any) => {
                const total = item.total || 0
                return sum + Number(total)
              }, 0)
            }

            if (!value || !totalValue) return true

            if (type === 'value') {
              const cleanValue = value.replace(/\D/g, '')
              const bonusValue = parseFloat(cleanValue) / 100
              const maxAllowed = totalValue * 1.0

              if (bonusValue > maxAllowed) {
                return false
              }
            }

            return true
          }
        ),
      startCycle: yup
        .number()
        .min(1, 'Ciclo inicial deve ser no mínimo 1')
        .default(1)
    }),
    otherwise: yup.object().optional()
  }),

  // Items on description validation - condicional baseada em isProductOrLink
  hasItemsOnDescription: yup.boolean().when('$isProductOrLink', {
    is: false, // Apenas para cobranças/recorrências
    then: yup.boolean().default(false),
    otherwise: yup.boolean().default(false) // Para produtos/links, sempre false
  }),

  // Internal identifiers validation
  hasIdentifyInternal: yup.boolean(),
  internalIdentifiers: yup
    .array()
    .of(
      yup.object().shape({
        name: yup.string().when('hasIdentifyInternal', {
          is: true,
          then: yup
            .string()
            .required('Preencha o nome do identificador interno.'),
          otherwise: yup.string().nullable()
        }),
        value: yup.string().when('hasIdentifyInternal', {
          is: true,
          then: yup
            .string()
            .required('Preencha o valor do identificador interno.'),
          otherwise: yup.string().nullable()
        })
      })
    )
    .default([]),

  // Invoice validation
  interestInvoice: yup.boolean(),
  copy_description: yup.boolean().nullable(),
  nfse_installment_scope: yup.string().when('interestInvoice', {
    is: true,
    then: yup.string().required('Escopo de parcelas da NFS-e é obrigatório.'),
    otherwise: yup.string().nullable()
  }),
  iss_retention: yup.string().when('interestInvoice', {
    is: true,
    otherwise: yup.string(),
    then: yup.string().required('Retenção de ISS é obrigatória.')
  }),
  service_list_code: yup.string().when('interestInvoice', {
    is: true,
    otherwise: yup.string().optional(),
    then: yup.string().required('Código de serviço é obrigatório.')
  }),
  amountCustom: yup.string().when('amount_type', {
    is: 'amount_custom',
    otherwise: yup.string().optional(),
    then: yup.string().required('Valor personalizado da NFS-e é obrigatório.')
  }),
  descriptionInvoice: yup
    .string()
    .nullable()
    .when(['interestInvoice', 'copy_description'], {
      is: (interestInvoice: boolean, copy_description: boolean) =>
        interestInvoice === true && copy_description === false,
      then: yup
        .string()
        .required('Descrição da NFS-e é obrigatória.')
        .nullable(),
      otherwise: yup.string().nullable()
    })
    .test(
      'variant-format',
      ({ value }) => validateNfseDescriptionVariants(value) as string,
      (value) => validateNfseDescriptionVariants(value ?? undefined) === null
    ),
  nfse_issuer_uuid: yup.string().when('interestInvoice', {
    is: true,
    otherwise: yup.string().optional(),
    then: yup.string().required('Emissor da NFS-e é obrigatório.')
  }),
  amount_type: yup.string().when('interestInvoice', {
    is: true,
    otherwise: yup.string(),
    then: yup
      .string()
      .required('Tipo de valor da NFS-e é obrigatório.')
      .test(
        'invalid-amount_type-for-issue_when',
        'A opção "Valor pago" só é válida quando a nota fiscal estiver para ser emitida "Quando o pagamento for efetuado"',
        function (value) {
          const { issue_when } = this.parent
          const isBlockedIssueWhen = [
            'on_custom_issued',
            'on_billing_issued'
          ].includes(issue_when)
          const isAmountPaid = value === 'amount_paid'

          return !(isBlockedIssueWhen && isAmountPaid)
        }
      )
  }),
  issue_when: yup.string().when('interestInvoice', {
    is: true,
    then: yup
      .string()
      .oneOf(
        ['on_billing_issued', 'on_billing_paid', 'on_custom_issued'],
        'Você deve selecionar uma das opções.'
      )
      .required('Momento de emissão da NFS-e é obrigatório.'),
    otherwise: yup.string()
  }),
  issue_mode: yup.string().when('issue_when', {
    is: 'on_custom_issued',
    then: yup.string().required('Selecione o tipo de configuração da NFS-e.'),
    otherwise: yup.string().optional()
  }),
  specific_day: yup.number().when(['issue_when', 'issue_mode'], {
    is: (issue_when: string, issue_mode: string) =>
      issue_when === 'on_custom_issued' && issue_mode === 'specific_day',
    then: yup
      .number()
      .required('Selecione o dia do mês para emissão da NFS-e.')
      .min(1, 'Dia da NFS-e deve ser entre 1 e 31.')
      .max(31, 'Dia da NFS-e deve ser entre 1 e 31.'),
    otherwise: yup.number().optional()
  }),
  specific_month_offset: yup.string().when(['issue_when', 'issue_mode'], {
    is: (issue_when: string, issue_mode: string) =>
      issue_when === 'on_custom_issued' && issue_mode === 'specific_day',
    then: yup.string().required('Selecione o mês de emissão da NFS-e.'),
    otherwise: yup.string().optional()
  }),
  // Validação para days_offset - usado quando issue_mode é before_due_date ou after_due_date
  days_offset: yup.string().when(['issue_when', 'issue_mode'], {
    is: (issue_when: string, issue_mode: string) =>
      issue_when === 'on_custom_issued' &&
      (issue_mode === 'before_due_date' || issue_mode === 'after_due_date'),
    then: yup
      .string()
      .required('Número de dias para emissão da NFS-e é obrigatório.')
      .test(
        'is-valid-number',
        'Dias para emissão da NFS-e deve ser um número válido maior que 0.',
        (value) => {
          if (!value) return false
          const num = parseInt(value, 10)
          return !isNaN(num) && num > 0
        }
      ),
    otherwise: yup.string().optional()
  }),
  // Legacy interval type validation - removed as not used in current UI
  // interval_type: obsolete field
  interval_value: yup
    .number()
    .when(['issue_when', 'issue_mode', 'interval_type'], {
      is: (issue_when: string, issue_mode: string) =>
        issue_when === 'on_custom_issued' &&
        (issue_mode === 'relative_days' || !issue_mode),
      then: yup
        .number()
        .typeError('Valor deve ser um número válido')
        .required('Valor do intervalo é obrigatório')
        .min(1, 'Valor deve ser maior que 0'),
      otherwise: yup.number().optional()
    }),
  // Legacy days field for backward compatibility
  days: yup.string().when(['issue_when', 'issue_mode'], {
    is: (issue_when: string, issue_mode: string) =>
      issue_when === 'on_custom_issued' &&
      (issue_mode === 'relative_days' || !issue_mode),
    then: yup.string().optional(), // Made optional since we use interval_value now
    otherwise: yup.string().optional()
  }),
  nature_operation: yup.string().when('interestInvoice', {
    is: true,
    otherwise: yup.string().optional(),
    then: yup.string().required('Natureza da operação é obrigatória.')
  }),

  // Plan validation
  isEnablePlan: yup.boolean(),
  plan: yup.string().when('isEnablePlan', {
    is: true,
    otherwise: yup.string().nullable(),
    then: yup.string().nullable().required('Plano é obrigatório.')
  }),

  // Types charges validation
  chargeType: yup.number().optional(),
  dueType: yup.string().when('$isProductOrLink', {
    is: true,
    then: yup.string().optional(),
    otherwise: yup.string().optional()
  }),
  dueDay: yup.number().when(['$isProductOrLink', 'dueType'], {
    is: (isProductOrLink: boolean, dueType: string) =>
      isProductOrLink &&
      (dueType === 'specified_charge' || dueType === 'delay_charge'),
    then: yup.number().required('Dia de vencimento é obrigatório.'),
    otherwise: yup.number().optional()
  }),
  proratedCharge: yup
    .boolean()
    .when(['$isProductOrLink', 'chargeType', 'dueType'], {
      is: (isProductOrLink: boolean, chargeType: number, dueType: string) =>
        isProductOrLink && chargeType === 1 && dueType === 'specified_charge',
      then: yup.boolean().optional(),
      otherwise: yup.boolean().optional()
    }),
  hasIssueDays: yup.boolean().default(false),
  issueDays: yup.number().when('hasIssueDays', {
    is: true,
    then: yup
      .number()
      .typeError('Deve ser um número válido.')
      .required('Quantidade de dias obrigatória.')
      .min(0, 'Valor mínimo é 0 dias.')
      .max(180, 'Valor máximo é 180 dias.')
      .integer('Deve ser um número inteiro.'),
    otherwise: yup.number().nullable().optional()
  }),
  issueBillingType: yup
    .string()
    .oneOf(['days_before_due_date', 'specific_day'])
    .default('days_before_due_date'),
  tags: yup.array().optional(),

  // Adjustment validation
  hasAdjustment: yup.boolean().default(false),
  adjustmentSettings: yup.object().when('hasAdjustment', {
    is: true,
    then: yup.object({
      adjustment_type: yup
        .string()
        .oneOf(['customized', 'ipca', 'igpm'], 'Tipo de reajuste inválido.')
        .required('Tipo de reajuste obrigatório.'),
      frequency: yup
        .string()
        .oneOf(['quarterly', 'semester', 'annual'], 'Frequência inválida.')
        .required('Frequência obrigatória.'),
      percentage_value: yup.string().when('adjustment_type', {
        is: 'customized',
        then: yup
          .string()
          .required('Percentual de reajuste obrigatório.')
          .test(
            'is-valid-percentage',
            'Percentual deve ser maior que 0.',
            (value) => {
              if (!value) return false
              const numValue = parseFloat(value.replace(',', '.'))
              return !isNaN(numValue) && numValue > 0
            }
          ),
        otherwise: yup.string().optional()
      }),
      next_adjustment_date: yup
        .string()
        .required('Data do próximo reajuste obrigatória.'),
      automatic_application: yup.boolean().default(true),
      // null = todos os itens (nada foi desmarcado). Uma lista vazia é coisa
      // diferente: o merchant desmarcou tudo, o que é engano — cair para "todos"
      // seria o oposto da intenção dele. O backend rejeita com a mesma leitura.
      billing_item_uuids: yup
        .array()
        .of(yup.string())
        .nullable()
        .test(
          'at-least-one-item',
          'Selecione ao menos um item para o reajuste.',
          (value) => value == null || value.length > 0
        )
    }),
    otherwise: yup.object().optional()
  })
})
